import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { PropsSelectRecursive } from "./types";
import { DropListSubCategorie } from "../../Context";
import { useTranslation } from "react-i18next";
import { useStyles } from "./styles";
import { useMemo } from "react";

const RecursiveSelect = ({ noDepartment, id, cacelEdition, handleEditSubcategory, handleAddSubcategory, categories, selectedCategorySelect, selectedDrops, selectedSubcategory, setSelectedCategorySelect, setSelectedDrops, setSelectedSubcategory, setTextValue, textValue }: PropsSelectRecursive) => {

  const { t } = useTranslation()
  const styles = useStyles()

  const handleCategoryChange = (categoryId: number) => {
    const category = categories.find((c) => c.id === categoryId) || null;
    setSelectedCategorySelect(category);
    setSelectedSubcategory(null);
    setSelectedDrops({});
    setTextValue("");
  };

  const handleSubcategoryChange = (subcategoryId: number) => {
    const sub = selectedCategorySelect?.subcategories.find((s) => s.id === subcategoryId) || null;
    setSelectedSubcategory(sub);
    setSelectedDrops({});
    setTextValue("");
  };

  const handleDropChange = (level: number, value: number, currentList: DropListSubCategorie[]) => {
    const selected = currentList.find((d) => d.value === value) || null;
    setSelectedDrops((prev) => {
      const updated = { ...prev };

      Object.keys(updated)
        .filter((key) => Number(key) >= level)
        .forEach((key) => delete updated[Number(key)]);
      updated[level] = selected;
      return updated;
    });
    setTextValue("");
  };

  const renderDropSelects = (dropList: DropListSubCategorie[], level = 0): JSX.Element | null => {
    if (!dropList?.length) return null;

    const selectedDrop = selectedDrops[level];
    const hasMoreChildren = !!selectedDrop?.dropList?.length;
    const hasTextRequest = Boolean(selectedDrop?.requestText);

    return (
      <>
        <FormControl sx={level % 2 == 0 ? styles.impar : styles.par}>
          <InputLabel>Nivel {level + 1}</InputLabel>
          <Select
            label={'SubLista'}
            value={selectedDrop?.value || ""}
            onChange={(e) => handleDropChange(level, Number(e.target.value), dropList)}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxWidth: '50%',
                },
              },
            }}
          >
            {dropList.map((d) => (
              <MenuItem
                key={d.value}
                value={d.value}
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                }}
              >
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Si hay hijos y no pide texto, seguimos recursivamente */}
        {hasMoreChildren && !hasTextRequest && renderDropSelects(selectedDrop!.dropList, level + 1)}

        {/* Si pide texto, mostramos el campo */}
        {hasTextRequest && (
          <TextField
            fullWidth
            label="Detalle*"
            variant="outlined"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            multiline
            rows={2}
            sx={{ marginLeft: "0px" }}
            inputProps={{
              maxLength: 250,
            }}
          />
        )}

      </>
    );
  };

  const shouldShowAddButtonFinal = useMemo(() => {
    if (id) return true;

    if (!selectedCategorySelect) return false;

    if (selectedSubcategory?.requestText) return true;

    if (
      selectedSubcategory &&
      (!selectedSubcategory.dropList ||
        selectedSubcategory.dropList.length === 0)
    ) {
      return true;
    }

    const deepestLevel = Object.keys(selectedDrops).length - 1;
    const lastSelected = selectedDrops[deepestLevel];

    if (!lastSelected) return false;

    const noMoreChildren = !lastSelected.dropList?.length;
    const requestsText = lastSelected.requestText;

    return noMoreChildren || requestsText;
  }, [selectedCategorySelect, selectedSubcategory, selectedDrops, id]);

  const isAddButtonDisabled = useMemo(() => {

    if (!selectedCategorySelect) return true;

    if (selectedSubcategory?.requestText) {
      return textValue.trim().length === 0;
    }

    if (
      selectedSubcategory &&
      (!selectedSubcategory.dropList || selectedSubcategory.dropList.length === 0)
    ) {
      return false;
    }

    const deepestLevel = Object.keys(selectedDrops).length - 1;
    const lastSelected = selectedDrops[deepestLevel];

    if (!lastSelected) return true;

    const noMoreChildren = !lastSelected.dropList?.length;
    const requiresText = lastSelected.requestText;
    const hasValidText = textValue.trim().length !== 0;

    if (requiresText) {
      return !hasValidText;
    }

    return !noMoreChildren;
  }, [selectedCategorySelect, selectedSubcategory, selectedDrops, id, textValue]);


  return (<>
    {noDepartment ?
      <Typography variant="no_data">{t('reportFolio.detail.general.selectAreaAndDepartmet')}</Typography>
      :
      <Box sx={styles.mainContainer}>

        <Typography>{t('reportFolio.detail.general.selectCategoryOrService')}</Typography>

        <Box sx={styles.selectContainer} >

          <FormControl sx={styles.impar}>
            <InputLabel>Categoría</InputLabel>
            <Select
              label="Categoría"
              value={selectedCategorySelect?.id || ""}
              onChange={(e) => handleCategoryChange(Number(e.target.value))}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedCategorySelect && (
            <FormControl sx={styles.par}>
              <InputLabel>Subcategoría</InputLabel>
              <Select
                label="Subcategoría"
                value={selectedSubcategory?.id || ""}
                onChange={(e) => handleSubcategoryChange(Number(e.target.value))}
              >
                {selectedCategorySelect.subcategories.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedSubcategory?.requestText && (
            <TextField
              fullWidth
              label="Detalle*"
              variant="outlined"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              multiline
              rows={2}
              sx={{ marginLeft: "0px" }}
              inputProps={{
                maxLength: 250,
              }}
            />
          )}

          {selectedSubcategory && !selectedSubcategory.requestText && renderDropSelects(selectedSubcategory.dropList)}

          {shouldShowAddButtonFinal && (
            <Box
              sx={styles.containerButtons}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => { id ? handleEditSubcategory(id) : handleAddSubcategory() }}
                disabled={isAddButtonDisabled}
              >
                {id ? t('general.edit') : t('general.add')}
              </Button>

              {id && <Button
                color="primary"
                onClick={cacelEdition}
              >
                {t('general.cancel')}
              </Button>}
            </Box>
          )}

        </Box>


      </Box>
    }
  </>

  );
};

export default RecursiveSelect;
